import type { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { SemicolonSafeUrlEncodingCodec } from './http-param-codec';

/**
 * Rebuilds every outgoing request's query params with SemicolonSafeUrlEncodingCodec, regardless
 * of whether the call site built them with `new HttpParams()` or a plain `params: {...}` object -
 * Angular converts the latter into HttpParams using the default (unsafe) codec before this
 * interceptor ever sees the request, so patching individual call sites isn't enough. See
 * SemicolonSafeUrlEncodingCodec for why this matters; keep this interceptor registered globally
 * in main.ts so no future HttpClient usage can silently reintroduce the bug.
 */
@Injectable()
export class HttpParamEncodingInterceptor implements HttpInterceptor {
	private static readonly encoder = new SemicolonSafeUrlEncodingCodec();

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		const keys = req.params.keys();
		if (keys.length === 0) {
			return next.handle(req);
		}

		let params = new HttpParams({ encoder: HttpParamEncodingInterceptor.encoder });
		for (const key of keys) {
			for (const value of req.params.getAll(key) ?? []) {
				params = params.append(key, value);
			}
		}

		return next.handle(req.clone({ params }));
	}
}
